import DetalleQuickviewExporter from "./DetalleQuickviewExporter";
import {Card, CardBody, Divider, Input, Spacer, Button, Select, SelectItem} from "@heroui/react";
import { Icon } from "@iconify/react";
import React, { useState, useEffect } from "react";
import { format, differenceInDays } from "date-fns";
import {CalendarDate, parseAbsoluteToLocal, parseDate} from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { CurrencyInput, DateInput, MonthSelect, YearInput, YearQuarterInput, StatusSelect } from "../../components/inputs";
import { apiClientExporter } from "../../services/apiClient";
import { toast } from "sonner";

function DetalleMaestraEditar({ maestraData }) {
    // Estado para controlar el modo de edición
    const [isEditing, setIsEditing] = useState(false);
    
    // Estado para almacenar el valor original de ofertas_de_cierre
    const [originalOfertasCierre, setOriginalOfertasCierre] = useState(maestraData.ofertas_de_cierre ?? "");
    
    // Estado para almacenar las opciones de causa
    const [causaOpciones, setCausaOpciones] = useState([]);
    
    // Estado para almacenar los valores editables
    const [formData, setFormData] = useState({
        rsk_ref: maestraData.rsk_ref ?? "",
        embarcador: maestraData.embarcador ?? "",
        nave: maestraData.nave ?? "",
        bl_awb: maestraData.bl_awb ?? "",
        container: maestraData.container ?? "",
        ref_seguro: maestraData.ref_seguro ?? "",
        monto_indemnizado_usd: maestraData.monto_indemnizado_usd ?? "",
        status: maestraData.status ?? "",
        porcentaje_recupero: maestraData.porcentaje_recupero ?? "",
        reclamo_usd: maestraData.reclamo_usd ?? "",
        reclamo_euros: maestraData.reclamo_euros ?? "",
        deducible: maestraData.deducible ?? "",
        claudio: maestraData.claudio ?? "",
        reclamo_a_terceros: maestraData.reclamo_a_terceros ?? "",
        contraparte: maestraData.contraparte ?? "",
        recuperado_usd: maestraData.recuperado_usd ?? "",
        recuperado_euros: maestraData.recuperado_euros ?? "",
        fecha_finiquito: maestraData.fecha_finiquito ?? "",
        time_bar: maestraData.time_bar ?? "",
        duracion_reclamo: maestraData.duracion_reclamo ?? "",
        proyeccion_usd: maestraData.proyeccion_usd ?? "",
        ofertas_de_cierre: maestraData.ofertas_de_cierre ?? "",
        fecha_oferta: maestraData.fecha_oferta ?? "",
        dias_que_faltan: maestraData.dias_que_faltan ?? "",
        comentario: maestraData.comentario ?? "",
        caso_year: maestraData.caso_year ?? "",
        caso_mes: maestraData.caso_mes ?? "",
        finiquito_year: maestraData.finiquito_year ?? "",
        tiempo_al_reclamo: maestraData.tiempo_al_reclamo ?? "",
        finiquito_mes: maestraData.finiquito_mes ?? "",
        especie: maestraData.especie ?? "",
        puerto_origen: maestraData.puerto_origen ?? "",
        puerto_destino: maestraData.puerto_destino ?? "",
        causa: maestraData.causa ?? "",
        fecha_creacion: maestraData.fecha_creacion ?? "",
        fecha_actualizacion: maestraData.fecha_actualizacion ?? "",
        a_cargo: maestraData.a_cargo ?? "",
        directo: maestraData.directo ?? "",
        id: maestraData.id ?? "",
        fecha_arribo: maestraData.fecha_arribo ?? "",
        time_extension: maestraData.time_extension ?? "",
        pais: maestraData.pais ?? "",
        quickview_percent_base: maestraData.quickview_percent_base
            ? (parseFloat(maestraData.quickview_percent_base) * 100).toString()
            : "",
        fecha_estimada: maestraData.fecha_estimada ?? "",
        eta: maestraData.eta ? format(new Date(maestraData.eta), "dd-MM-yyyy") : "",
        ata: maestraData.ata ? format(new Date(maestraData.ata), "dd-MM-yyyy") : "",
    });

    // Si maestraData cambia, actualizar formData (incluyendo la conversión de quickview_percent_base)
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            quickview_percent_base: maestraData.quickview_percent_base
                ? (parseFloat(maestraData.quickview_percent_base) * 100).toString()
                : "",
        }));
    }, [maestraData.quickview_percent_base]);

    // Cargar las opciones de causa desde el backend
    useEffect(() => {
        const fetchCausaOpciones = async () => {
            try {
                const response = await apiClientExporter.get('/exporter/causa/opciones');
                let opciones = response.data;
                
                // Agregar el valor actual de causa si existe y no está en las opciones estándar
                if (maestraData.causa && maestraData.causa.trim() !== "" && 
                    !opciones.some(opcion => opcion.value === maestraData.causa)) {
                    opciones = [
                        {
                            id: 'current',
                            label: maestraData.causa,  // ✅ Sin sufijo "(valor actual)"
                            value: maestraData.causa
                        },
                        ...opciones
                    ];
                }
                
                setCausaOpciones(opciones);
            } catch (error) {
                console.error('Error al cargar las opciones de causa:', error);
                toast.error('Error al cargar las opciones de causa');
            }
        };

        fetchCausaOpciones();
    }, [maestraData.causa]);

    // Función para determinar el color basado en los días que faltan
    const getDiasColor = (diasQueFaltan) => {
        if (diasQueFaltan === "N/A" || diasQueFaltan === "Vencido") {
            return "default"; // Gris para N/A y Vencido
        }
        
        const dias = parseInt(diasQueFaltan);
        if (isNaN(dias)) return "default";
        
        if (dias <= 30) {
            return "danger"; // Rojo para 30 días o menos
        } else if (dias <= 60) {
            return "warning"; // Amarillo para 31-60 días
        } else {
            return "success"; // Verde para más de 60 días
        }
    };

    // Función para calcular automáticamente los días que faltan
    const calcularDiasQueFaltan = (timeBar, timeExtension) => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0); // Resetear a inicio del día para comparación precisa

        let fechaReferencia = null;

        // Si existe time_extension, usarla como fecha de referencia
        if (timeExtension) {
            fechaReferencia = new Date(timeExtension);
            console.log("DetalleMaestraEditar - Usando time_extension:", timeExtension, "fechaReferencia:", fechaReferencia);
        }
        // Si no existe time_extension pero existe time_bar, usar time_bar
        else if (timeBar) {
            // timeBar puede venir en formato dd-MM-yyyy
            if (timeBar.includes("-")) {
                const [day, month, year] = timeBar.split("-");
                // Crear la fecha en zona horaria local para evitar problemas de conversión
                fechaReferencia = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            } else {
                // Si es una fecha directa
                fechaReferencia = new Date(timeBar);
            }
            console.log("DetalleMaestraEditar - Usando time_bar:", timeBar, "fechaReferencia:", fechaReferencia);
        }

        if (!fechaReferencia || isNaN(fechaReferencia.getTime())) {
            console.log("DetalleMaestraEditar - No se pudo calcular fecha de referencia");
            return "N/A";
        }

        // Crear fechas sin zona horaria usando solo la fecha
        const fechaReferenciaSinTZ = new Date(fechaReferencia.getFullYear(), fechaReferencia.getMonth(), fechaReferencia.getDate());
        const hoySinTZ = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
        
        const diasDiferencia = differenceInDays(fechaReferenciaSinTZ, hoySinTZ);

        console.log("DetalleMaestraEditar - Cálculo final:", {
            fechaReferencia,
            fechaReferenciaSinTZ,
            hoySinTZ,
            diasDiferencia
        });

        if (diasDiferencia <= 0) {
            return "Vencido";
        } else {
            return diasDiferencia.toString();
        }
    };

    // --- CÁLCULOS AUTOCALCULADOS ---
    useEffect(() => {
        // Calcular time_bar: Fecha de arribo + 730 días
        let timeBar = "";
        let timeBarDate = null;
        if (formData.fecha_arribo) {
            const fechaArribo = new Date(formData.fecha_arribo);
            fechaArribo.setDate(fechaArribo.getDate() + 730);
            timeBar = format(fechaArribo, "dd-MM-yyyy");
            timeBarDate = fechaArribo;
        }

        // Calcular días que faltan (usa time_extension o time_bar)
        const diasQueFaltan = calcularDiasQueFaltan(timeBar, formData.time_extension);

        // Calcular claudio
        let claudio = "";
        const deducible = parseFloat(formData.deducible);
        const reclamoUsd = parseFloat(formData.reclamo_usd);
        const montoIndemnizado = parseFloat(formData.monto_indemnizado_usd);
        if (!isNaN(deducible) && deducible === 0) {
            claudio = !isNaN(reclamoUsd) ? reclamoUsd : "";
        } else {
            claudio = !isNaN(montoIndemnizado) ? montoIndemnizado : "";
        }

        // Calcular proyeccion_usd
        let proyeccionUsd = "";
        const quickviewPercent = parseFloat(formData.quickview_percent_base);
        if (!isNaN(reclamoUsd) && !isNaN(quickviewPercent)) {
            proyeccionUsd = (reclamoUsd * quickviewPercent / 100).toFixed(2);
        }

        // Calcular % recupero
        let porcentajeRecupero = "";
        const recuperadoUsd = parseFloat(formData.recuperado_usd);
        if (!isNaN(montoIndemnizado) && montoIndemnizado > 0 && !isNaN(recuperadoUsd)) {
            porcentajeRecupero = ((recuperadoUsd / montoIndemnizado) * 100).toFixed(2);
        } else if (!isNaN(reclamoUsd) && reclamoUsd > 0 && !isNaN(recuperadoUsd)) {
            porcentajeRecupero = ((recuperadoUsd / reclamoUsd) * 100).toFixed(2);
        }

        // Calcular año y mes de caso (fecha_arribo)
        let casoYear = "";
        let casoMes = "";
        if (formData.fecha_arribo) {
            const fechaArribo = new Date(formData.fecha_arribo);
            casoYear = fechaArribo.getFullYear().toString();
            casoMes = (fechaArribo.getMonth() + 1).toString();
        }

        // Calcular tiempo al reclamo (reclamo_a_terceros - fecha_arribo) / 30
        let tiempoAlReclamo = "";
        if (formData.reclamo_a_terceros && formData.fecha_arribo) {
            const f1 = new Date(formData.reclamo_a_terceros);
            const f2 = new Date(formData.fecha_arribo);
            tiempoAlReclamo = ((f1 - f2) / (1000 * 60 * 60 * 24 * 30)).toFixed(2);
        }

        // Calcular duración reclamo (fecha_finiquito - reclamo_a_terceros) / 30
        let duracionReclamo = "";
        if (formData.fecha_finiquito && formData.reclamo_a_terceros) {
            const f1 = new Date(formData.fecha_finiquito);
            const f2 = new Date(formData.reclamo_a_terceros);
            duracionReclamo = ((f1 - f2) / (1000 * 60 * 60 * 24 * 30)).toFixed(2);
        }

        // Calcular año y mes de finiquito
        let finiquitoYear = "";
        let finiquitoMes = "";
        if (formData.fecha_finiquito) {
            const fechaFiniquito = new Date(formData.fecha_finiquito);
            finiquitoYear = fechaFiniquito.getFullYear().toString();
            finiquitoMes = (fechaFiniquito.getMonth() + 1).toString();
        }

        setFormData(prevData => ({
            ...prevData,
            time_bar: timeBar,
            dias_que_faltan: diasQueFaltan,
            claudio: claudio,
            proyeccion_usd: proyeccionUsd,
            porcentaje_recupero: porcentajeRecupero,
            caso_year: casoYear,
            caso_mes: casoMes,
            tiempo_al_reclamo: tiempoAlReclamo,
            duracion_reclamo: duracionReclamo,
            finiquito_year: finiquitoYear,
            finiquito_mes: finiquitoMes
        }));
    // eslint-disable-next-line
    }, [formData.deducible, formData.reclamo_usd, formData.monto_indemnizado_usd, formData.quickview_percent_base, formData.recuperado_usd, formData.fecha_arribo, formData.reclamo_a_terceros, formData.fecha_finiquito, formData.time_extension]);

    // Manejador para actualizar los valores de los campos
    const handleInputChange = (field, value) => {
        console.log("field", field);
        console.log("value", value);
        
        // Lógica especial para ofertas_de_cierre
        if (field === "ofertas_de_cierre") {
            const fechaActual = format(new Date(), "yyyy-MM-dd");
            
            setFormData((prevData) => {
                const newData = {
                    ...prevData,
                    [field]: value,
                };
                
                // Si el valor cambió y es diferente del original, actualizar fecha_oferta
                if (value !== originalOfertasCierre) {
                    newData.fecha_oferta = fechaActual;
                } else {
                    // Si vuelve al valor original, restaurar la fecha original
                    newData.fecha_oferta = maestraData.fecha_oferta ?? "";
                }
                
                return newData;
            });
        } else {
            setFormData((prevData) => {
                const newData = { ...prevData, [field]: value };
                
                // Automatización del Status
                if (field === "recuperado_usd") {
                    const recuperadoValue = parseFloat(value) || 0;
                    
                    console.log("Automatización Status - Recuperado USD:", {
                        field,
                        value,
                        recuperadoValue,
                        condition: recuperadoValue === 0
                    });
                    
                    // Si recuperado es 0, cambiar a "Cerrado" siempre
                    if (recuperadoValue === 0) {
                        newData.status = "Cerrado";
                        console.log("Cambiando status a 'Cerrado'");
                        toast.success("Status automatically changed to 'Closed'", {
                            duration: 3000,
                            position: "top-right",
                        });
                    }
                }
                
                return newData;
            });
        }
    };

    const handleDateChange = (field, newValue) => {
        console.log("field", field);
        console.log("value", newValue);
        const formattedDate = newValue
            ? format(new Date(newValue.year, newValue.month - 1, newValue.day), "yyyy-MM-dd")
            : null;

        setFormData((prevData) => {
            const newData = { ...prevData, [field]: formattedDate };
            
            // Automatización del Status para fecha de finiquito
            if (field === "fecha_finiquito") {
                const recuperadoValue = parseFloat(prevData.recuperado_usd) || 0;
                
                console.log("Automatización Status - Fecha Finiquito:", {
                    field,
                    formattedDate,
                    recuperadoValue,
                    condition: formattedDate && recuperadoValue > 0
                });
                
                // Si se establece una fecha de finiquito y recuperado NO es 0, cambiar a "Finiquito Firmado"
                if (formattedDate && recuperadoValue > 0) {
                    newData.status = "Finiquito Firmado";
                    console.log("Cambiando status a 'Finiquito Firmado'");
                    toast.success("Status automatically changed to 'Signed Settlement'", {
                        duration: 3000,
                        position: "top-right",
                    });
                }
                // Si se establece una fecha de finiquito y recuperado ES 0, NO cambiar el status
                // (el status ya debería ser "Cerrado" por la regla anterior)
            }
            
            return newData;
        });
    };

    // Manejador para guardar los cambios
    const handleSave = async () => {
        try {
            const token = localStorage.getItem("exporterAuthToken");

            // Limpiar datos antes de enviar - convertir strings vacíos a null para campos numéricos
            // Eliminar campos autocalculados del payload
            const {
                claudio,
                proyeccion_usd,
                time_bar,
                porcentaje_recupero,
                caso_year,
                caso_mes,
                tiempo_al_reclamo,
                duracion_reclamo,
                finiquito_year,
                finiquito_mes,
                dias_que_faltan,
                ...rest
            } = formData;

            const cleanedData = {
                ...rest,
                monto_indemnizado_usd: rest.monto_indemnizado_usd === "" ? null : rest.monto_indemnizado_usd,
                deducible: rest.deducible === "" ? null : rest.deducible,
                reclamo_usd: rest.reclamo_usd === "" ? null : rest.reclamo_usd,
                reclamo_euros: rest.reclamo_euros === "" ? null : rest.reclamo_euros,
                recuperado_usd: rest.recuperado_usd === "" ? null : rest.recuperado_usd,
                recuperado_euros: rest.recuperado_euros === "" ? null : rest.recuperado_euros,
                ofertas_de_cierre: rest.ofertas_de_cierre === "" ? null : rest.ofertas_de_cierre,
                id: rest.id === "" ? null : rest.id,
                quickview_percent_base: rest.quickview_percent_base
                    ? (parseFloat(rest.quickview_percent_base) / 100).toFixed(4)
                    : null,
            };

            console.log("Datos a enviar al backend:", cleanedData);

            const response = await apiClientExporter.put('/exporter/maestra/recuperos', cleanedData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            console.log("Datos guardados exitosamente:", response.data);
            toast.success("Data successfully saved", {
                duration: 3000,
                position: "top-right",
            });
            setIsEditing(false);
        } catch (error) {
            console.error("Error al guardar los datos:", error);
            toast.error("Error saving data. Please try again.", {
                duration: 4000,
                position: "top-right",
            });
        }
    };

    // Manejador para alternar el modo de edición
    const toggleEditMode = () => {
        setIsEditing(!isEditing);
    };

    const identificationFields = [
        { icon: "lucide:hash", label: "Ref. RSK", fieldName: "rsk_ref", value: formData.rsk_ref },
        { icon: "lucide:hash", label: "Exportador (ex embarcador)", fieldName: "embarcador", value: formData.embarcador },
        { icon: "lucide:ship", label: "Nave", fieldName: "nave", value: formData.nave },
        { icon: "lucide:hash", label: "BL/AWB", fieldName: "bl_awb", value: formData.bl_awb },
        { icon: "lucide:container", label: "Container", fieldName: "container", value: formData.container },
        // { icon: "lucide:hash", label: "ID", fieldName: "id", value: formData.id }, // OCULTO TEMPORALMENTE
        { icon: "lucide:hash", label: "Fecha Creación", fieldName: "fecha_creacion", value: formData.fecha_creacion, tipo: "fecha" },
        { icon: "lucide:hash", label: "Fecha Actualización", fieldName: "fecha_actualizacion", value: formData.fecha_actualizacion, tipo: "fecha" },
        { icon: "lucide:hash", label: "Especie", fieldName: "especie", value: formData.especie, tipo: "readonly" },
        { icon: "lucide:hash", label: "Directo", fieldName: "directo", value: formData.directo, tipo: "readonly" },
    ];

    const claimFields = [
        { icon: "lucide:hash", label: "Ref. Seguro", fieldName: "ref_seguro", value: formData.ref_seguro },
        { icon: "lucide:dollar-sign", label: "Monto Indemnizado USD", fieldName: "monto_indemnizado_usd", value: formData.monto_indemnizado_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:hash", label: "Status", fieldName: "status", value: formData.status, tipo: "status" },
        { icon: "lucide:percent", label: "% Recupero (auto)", fieldName: "porcentaje_recupero", value: formData.porcentaje_recupero, tipo: "readonly" },
        { icon: "lucide:dollar-sign", label: "Reclamo USD", fieldName: "reclamo_usd", value: formData.reclamo_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:euro", label: "Reclamo Euros", fieldName: "reclamo_euros", value: formData.reclamo_euros, tipo: "moneda", currency: "EUR" },
        { icon: "lucide:dollar-sign", label: "Deducible", fieldName: "deducible", value: formData.deducible, tipo: "moneda", currency: "USD" },
        // { icon: "lucide:dollar-sign", label: "Claudio (auto)", fieldName: "claudio", value: formData.claudio, tipo: "readonly", currency: "USD" }, // OCULTO TEMPORALMENTE
        { icon: "lucide:calendar", label: "Reclamo a Terceros", fieldName: "reclamo_a_terceros", value: formData.reclamo_a_terceros, tipo: "fecha" },
        { icon: "lucide:ship-wheel", label: "Contraparte", fieldName: "contraparte", value: formData.contraparte },
        { icon: "lucide:dollar-sign", label: "Recuperado USD", fieldName: "recuperado_usd", value: formData.recuperado_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:dollar-sign", label: "Recuperado Euros", fieldName: "recuperado_euros", value: formData.recuperado_euros, tipo: "moneda", currency: "EUR" },
        { icon: "lucide:hash", label: "Time bar (auto)", fieldName: "time_bar", value: formData.time_bar, tipo: "fecha" },
        { icon: "lucide:hash", label: "Time Extension", fieldName: "time_extension", value: formData.time_extension, tipo: "fecha" },
        { icon: "lucide:hash", label: "Días que faltan (auto)", fieldName: "dias_que_faltan", value: formData.dias_que_faltan, tipo: "readonly" },
        { icon: "lucide:hash", label: "Fecha Finiquito", fieldName: "fecha_finiquito", value: formData.fecha_finiquito, tipo: "fecha" },
        { icon: "lucide:hash", label: "Duración Reclamo (auto)", fieldName: "duracion_reclamo", value: formData.duracion_reclamo, tipo: "readonly" },
        { icon: "lucide:dollar-sign", label: "Ofertas de Cierre", fieldName: "ofertas_de_cierre", value: formData.ofertas_de_cierre, tipo: "moneda", currency: "USD" },
        { icon: "lucide:hash", label: "Fecha Oferta", fieldName: "fecha_oferta", value: formData.fecha_oferta, tipo: "fecha" },
        { icon: "lucide:calendar", label: "ETA", fieldName: "eta", value: formData.eta, tipo: "fecha" },
        { icon: "lucide:calendar", label: "ATA", fieldName: "ata", value: formData.ata, tipo: "fecha" },
        { icon: "lucide:hash", label: "Puerto origen", fieldName: "puerto_origen", value: formData.puerto_origen },
        { icon: "lucide:hash", label: "Puerto destino", fieldName: "puerto_destino", value: formData.puerto_destino },
        { icon: "lucide:hash", label: "A cargo", fieldName: "a_cargo", value: formData.a_cargo },
    ];

    const projectionFields = [
        { icon: "lucide:percent", label: "Quickview (% base)", fieldName: "quickview_percent_base", value: formData.quickview_percent_base },
        { icon: "lucide:dollar-sign", label: "Proyección USD (auto)", fieldName: "proyeccion_usd", value: formData.proyeccion_usd, tipo: "moneda", currency: "USD" },
        { icon: "lucide:hash", label: "Fecha Estimada", fieldName: "fecha_estimada", value: formData.fecha_estimada, tipo: "yearquarter" },
    ];

    const otherFields = [
        
        { icon: "lucide:hash", label: "Comentario", fieldName: "comentario", value: formData.comentario },
        { icon: "lucide:calendar-fold", label: "Año Caso (auto)", fieldName: "caso_year", value: formData.caso_year, tipo: "readonly" },
        { icon: "lucide:calendar-1", label: "Mes Caso (auto)", fieldName: "caso_mes", value: formData.caso_mes, tipo: "readonly" },
        { icon: "lucide:calendar-fold", label: "Año Finiquito (auto)", fieldName: "finiquito_year", value: formData.finiquito_year, tipo: "readonly" },
        { icon: "lucide:calendar-1", label: "Mes Finiquito (auto)", fieldName: "finiquito_mes", value: formData.finiquito_mes, tipo: "readonly" },
        { icon: "lucide:hash", label: "Tiempo al Reclamo (auto)", fieldName: "tiempo_al_reclamo", value: formData.tiempo_al_reclamo, tipo: "readonly" },
        { icon: "lucide:hash", label: "Causa", fieldName: "causa", value: formData.causa, tipo: "causa" },
        { icon: "lucide:hash", label: "Fecha Arribo", fieldName: "fecha_arribo", value: formData.fecha_arribo, tipo: "fecha" },
        { icon: "lucide:flag", label: "País", fieldName: "pais", value: formData.pais },
    ];

    // Función para renderizar el campo apropiado según su tipo
    const renderField = (field, index) => {
        const commonProps = {
            label: field.label,
            value: field.value,
            onChange: (value) => handleInputChange(field.fieldName, value),
            isReadOnly: field.tipo === "fecha" ? !isEditing : !isEditing,
            icon: field.icon,
        };

        // Campo especial: time_bar (auto) - solo lectura, sin calendario
        if (field.fieldName === "time_bar") {
            return (
                <div key={index} className="w-full">
                    <Input
                        label={field.label}
                        value={field.value}
                        isReadOnly={true}
                        startContent={
                            <Icon
                                icon={field.icon}
                                className="text-default-400 pointer-events-none flex-shrink-0"
                            />
                        }
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                    />
                </div>
            );
        }

        // Campo especial: eta y ata - solo lectura, formato fecha
        if (field.fieldName === "eta" || field.fieldName === "ata") {
            return (
                <div key={index} className="w-full">
                    <Input
                        label={field.label}
                        value={field.value}
                        isReadOnly={true}
                        startContent={
                            <Icon
                                icon={field.icon}
                                className="text-default-400 pointer-events-none flex-shrink-0"
                            />
                        }
                        classNames={{
                            label: "text-sm font-medium",
                            input: "text-sm",
                        }}
                    />
                </div>
            );
        }

        // Campo especial: time_extension - editable y con botón para limpiar
        if (field.fieldName === "time_extension") {
            return (
                <div key={index} className="w-full flex items-end gap-2">
                    <DateInput
                        {...commonProps}
                        onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                        isReadOnly={!isEditing}
                    />
                    {isEditing && (
                        <Button
                            size="sm"
                            isIconOnly
                            color="default"
                            variant="light"
                            className="rounded-full border border-gray-300 shadow-none hover:bg-gray-100"
                            onPress={() => handleInputChange(field.fieldName, "")}
                            title="Clean"
                        >
                            <Icon icon="lucide:x" className="text-lg" />
                        </Button>
                    )}
                </div>
            );
        }

        switch (field.tipo) {
            case "moneda":
                // Lógica especial para el campo recuperado_usd
                if (field.fieldName === "recuperado_usd") {
                    return (
                        <div key={index} className="w-full">
                            <CurrencyInput
                                {...commonProps}
                                currency={field.currency || "USD"}
                                placeholder={!field.value || field.value === "" ? "En proceso" : undefined}
                            />
                        </div>
                    );
                }
                // Lógica especial para campos de moneda que deben ser readonly
                if (field.fieldName === "proyeccion_usd") {
                    return (
                        <div key={index} className="w-full">
                            <CurrencyInput
                                label={field.label}
                                value={field.value}
                                onChange={() => {}} // Función vacía para hacer readonly
                                isReadOnly={true}
                                icon={field.icon}
                                currency={field.currency || "USD"}
                            />
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <CurrencyInput
                            {...commonProps}
                            currency={field.currency || "USD"}
                        />
                    </div>
                );
            case "fecha":
                return (
                    <div key={index} className="w-full">
                        <DateInput
                            {...commonProps}
                            onChange={(newValue) => handleDateChange(field.fieldName, newValue)}
                            isReadOnly={!isEditing}
                        />
                    </div>
                );
            case "mes":
                return (
                    <div key={index} className="w-full">
                        <MonthSelect
                            {...commonProps}
                        />
                    </div>
                );
            case "año":
                return (
                    <div key={index} className="w-full">
                        <YearInput
                            {...commonProps}
                        />
                    </div>
                );
            case "yearquarter":
                return (
                    <div key={index} className="w-full">
                        <YearQuarterInput
                            {...commonProps}
                        />
                    </div>
                );
            case "status":
                return (
                    <div key={index} className="w-full">
                        <StatusSelect
                            {...commonProps}
                        />
                    </div>
                );
            case "causa":
                return (
                    <div key={index} className="w-full">
                        <Select
                            label="Causa"
                            selectedKeys={formData.causa ? [formData.causa] : []}
                            onSelectionChange={(keys) => {
                                const selectedKey = Array.from(keys)[0];
                                console.log("Causa seleccionada:", selectedKey);
                                handleInputChange("causa", selectedKey || "");
                            }}
                            isDisabled={!isEditing}
                            placeholder="Seleccionar causa"
                            classNames={{
                                label: "text-sm font-medium",
                                trigger: "text-sm",
                            }}
                        >
                            {/* Mostrar todas las opciones disponibles (incluyendo el valor actual) */}
                            {causaOpciones.map((causa) => (
                                <SelectItem key={causa.value} value={causa.value}>
                                    {causa.label}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                );
            case "readonly":
                // Lógica especial para el campo dias_que_faltan
                if (field.fieldName === "dias_que_faltan") {
                    const color = getDiasColor(field.value);
                    return (
                        <div key={index} className="w-full">
                            <Input
                                {...commonProps}
                                isReadOnly={true}
                                color={color}
                                startContent={
                                    <Icon
                                        icon={field.icon}
                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                    />
                                }
                                classNames={{
                                    label: "text-sm font-medium",
                                    input: "text-sm",
                                }}
                            />
                        </div>
                    );
                }
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            isReadOnly={true}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
            default:
                return (
                    <div key={index} className="w-full">
                        <Input
                            {...commonProps}
                            onChange={(e) => handleInputChange(field.fieldName, e.target.value)}
                            startContent={
                                <Icon
                                    icon={field.icon}
                                    className="text-default-400 pointer-events-none flex-shrink-0"
                                />
                            }
                            classNames={{
                                label: "text-sm font-medium",
                                input: "text-sm",
                            }}
                        />
                    </div>
                );
        }
    };

    return (
        <div>
            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Identification (Read Only)</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {identificationFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Claim</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {claimFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Proyección (Budget)</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {projectionFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />

            <div className="max-w-full">
                <Card className="w-full">
                    <CardBody>
                        <h2 className="text-2xl font-bold">Other fields</h2>
                        <Divider className="my-4" />
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {otherFields.map((field, index) => renderField(field, index))}
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Spacer />
            
            <div className="flex justify-between">
                <Button
                    variant="ghost"
                    color="primary"
                    onPress={toggleEditMode}
                    startContent={<Icon icon={isEditing ? "lucide:lock" : "lucide:unlock"} />}
                >
                    {isEditing ? "Lock edition" : "Edit documents"}
                </Button>

                <Button color="primary" onPress={handleSave} isDisabled={!isEditing}>
                    Save
                </Button>
            </div>
        </div>
    );
}

export default DetalleMaestraEditar;